'use client';

import {
  type ChangeEvent,
  type ChangeEventHandler,
  type FC,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
  type SVGProps,
  useCallback,
  useId,
  useImperativeHandle,
  useMemo,
} from 'react';
import type { Placement } from '@floating-ui/react-dom';
import { clsx } from 'clsx';
import { useSelect } from 'downshift';
import ClearIcon from '../../../icons/react/icon-clear';
import DisclosureDownIcon from '../../../icons/react/icon-disclosure-down';
import DisclosureDownSmallIcon from '../../../icons/react/icon-disclosure-down-small';
import InfoIcon from '../../../icons/react/icon-info';
import { HorizontalStack } from '../../alignment/horizontal-stack';
import { VerticalStack } from '../../alignment/vertical-stack';
import { PlainIconButton } from '../../button/plain-icon/plain-icon-button';
import { Tooltip } from '../../tooltip/tooltip';
import { ErrorMessage } from '../error-message/error-message';
import { InputHint } from '../input-hint/input-hint';
import { Label } from '../label/label';
import {
  InputDropdown,
  type InputDropdownOption,
  type InputDropdownPagination,
  inputDropdownOptionToString,
  useFloatingDropdown,
} from '../shared/dropdown/input-dropdown';
import { useFocusWithin } from '../shared/use-focus-within';
import inputStyles from '../input.module.scss';
import styles from './select-input.module.scss';

export interface SelectInputProps<T extends string = string> extends HTMLAttributes<HTMLSelectElement> {
  allowClear?: boolean;
  disabled?: boolean;
  error?: string;
  ExpandIconElement?: FC<SVGProps<SVGSVGElement>>;
  fullWidth?: boolean;
  hideLabel?: boolean;
  hint?: ReactNode;
  info?: ReactNode;
  label: string;
  matchWidth?: boolean;
  menuClassName?: string;
  name: string;
  options: InputDropdownOption<T>[];
  onChange?: ChangeEventHandler<HTMLSelectElement>;
  pagination?: InputDropdownPagination;
  placeholder?: ReactNode;
  placement?: Placement;
  prefixIcon?: ReactNode;
  ref?: Ref<HTMLSelectElement>;
  required?: boolean;
  selectedItemRenderer?: (option: InputDropdownOption<T>) => ReactNode;
  touched?: boolean;
  triggerClassName?: string;
  value: T;
  variant?: 'default' | 'button-light-small' | 'button-light-medium';
}

export function SelectInput({
  className,
  disabled,
  error,
  variant = 'default',
  ExpandIconElement = variant?.includes('button') ? DisclosureDownSmallIcon : DisclosureDownIcon,
  fullWidth,
  hideLabel,
  hint,
  id: providedId,
  info,
  label,
  matchWidth = true,
  menuClassName,
  name,
  onChange,
  options,
  pagination,
  placeholder,
  placement,
  prefixIcon,
  ref,
  required,
  selectedItemRenderer,
  touched,
  triggerClassName,
  value,
  allowClear = variant !== 'button-light-small' && variant !== 'button-light-medium' && !required,
  ...rest
}: SelectInputProps) {
  const [hasFocus, handleFocusWithin, handleBlurWithin, setFocusWithin] = useFocusWithin();
  const selectedItem = options.find((option) => option.value === value) || null;
  const generatedId = useId();
  const id = providedId || generatedId;
  const labelId = `${id}-label`;
  const menuId = `${id}-menu`;
  const toggleButtonId = `${id}-toggle`;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const showError = Boolean(touched && error && value);
  const { getItemProps, getMenuProps, getLabelProps, getToggleButtonProps, highlightedIndex, isOpen, closeMenu, selectItem, toggleMenu } = useSelect({
    id,
    menuId,
    labelId,
    toggleButtonId,
    items: options,
    itemToString: inputDropdownOptionToString,
    selectedItem: selectedItem || null,
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
      onChange?.({
        target: {
          name,
          value: newSelectedItem && 'value' in newSelectedItem ? newSelectedItem.value || '' : '',
        },
      } as ChangeEvent<HTMLSelectElement>);

      closeMenu();
    },
  });

  const { refs, floatingStyles } = useFloatingDropdown(options.length, isOpen, matchWidth, placement);
  const listboxProps = useMemo(
    () => ({
      ...getMenuProps({ ref: refs.setFloating, disabled }),
      style: floatingStyles,
      hidden: !isOpen,
    }),
    [isOpen, floatingStyles, getMenuProps, refs.setFloating, disabled],
  );

  useImperativeHandle(
    ref,
    () =>
      ({
        id,
        value,
        name,
        ...refs.reference,
      }) as unknown as HTMLSelectElement,
    [id, name, refs.reference, value],
  );

  const triggerHasValue = Boolean(selectedItem || placeholder || isOpen || hasFocus);

  const handleClear = useCallback(() => {
    selectItem(null);
    setFocusWithin(false);
  }, [selectItem, setFocusWithin]);

  const allItemsHavePrefix = options.every((option) => option.prefix);

  return (
    <VerticalStack
      className={clsx(
        inputStyles.input,
        styles.selectInput,
        triggerHasValue && styles.hasValue,
        disabled && inputStyles.isDisabled,
        disabled && styles.isDisabled,
        fullWidth && styles.isFullWidth,
        hideLabel && styles.hideLabel,
        variant && styles[`variant-${variant}`],
        (prefixIcon || allItemsHavePrefix) && styles.withPrefixIcon,
        className,
      )}
      onBlurCapture={handleBlurWithin}
      onFocusCapture={handleFocusWithin}
    >
      <VerticalStack ref={refs.setReference}>
        <HorizontalStack className={clsx(inputStyles.activator, styles.triggerContainer, styles.inputElement)}>
          <Label
            {...getLabelProps({ label, className: clsx(styles.label, triggerHasValue && styles.isFloating) })}
            hasError={showError}
            isFloating={triggerHasValue}
            isHidden={hideLabel}
            required={required}
          />

          <HorizontalStack align="space-between" blockAlign="center" className={styles.triggerSelection} wrap="nowrap">
            <button
              aria-describedby={hint ? hintId : undefined}
              aria-errormessage={showError ? errorId : undefined}
              className={clsx(styles.trigger, showError && styles.error, triggerClassName)}
              type="button"
              {...getToggleButtonProps({
                ...(rest as unknown as HTMLAttributes<HTMLButtonElement>),
                disabled,
                required,
              })}
            >
              <HorizontalStack align="start" blockAlign="center" className={styles.item} gap={2} wrap="nowrap">
                {prefixIcon ? <span className={styles.prefixIcon}>{prefixIcon}</span> : null}
                {selectedItem ? (
                  selectedItemRenderer ? (
                    <span className={styles.option}>{selectedItemRenderer(selectedItem)}</span>
                  ) : (
                    <>
                      {selectedItem.prefix && variant !== 'button-light-small' ? (
                        <span className={styles.prefixIcon}>{selectedItem.prefix}</span>
                      ) : null}
                      <span className={styles.option}>{selectedItem.label}</span>
                      {/* Mirror the option's suffix in the trigger once it is selected. */}
                      {selectedItem.suffix ? <span className={styles.suffix}>{selectedItem.suffix}</span> : null}
                    </>
                  )
                ) : (
                  <span className={styles.placeholder}>{placeholder || <span>&zwnj;</span>}</span>
                )}
              </HorizontalStack>
            </button>

            <HorizontalStack blockAlign="center" className={styles.iconContainer} gap={4} wrap="nowrap">
              {allowClear && !disabled && selectedItem ? (
                <button aria-controls={id} aria-label="Clear selection" className={styles.clearButton} onClick={handleClear} type="button">
                  <ClearIcon aria-hidden />
                </button>
              ) : null}

              {info ? (
                <Tooltip
                  activator={
                    <PlainIconButton>
                      <InfoIcon aria-hidden height={16} />
                    </PlainIconButton>
                  }
                  maxWidth={136}
                  side="top"
                  size="small"
                >
                  {info}
                </Tooltip>
              ) : null}

              <ExpandIconElement
                aria-hidden
                className={clsx(styles.toggleIcon, isOpen && styles.isOpen)}
                height={16}
                onClick={disabled ? undefined : toggleMenu}
              />
            </HorizontalStack>
          </HorizontalStack>
        </HorizontalStack>

        {hint ? (
          <InputHint className={styles.inputElement} id={hintId}>
            {hint}
          </InputHint>
        ) : null}
        {showError ? (
          <ErrorMessage className={styles.inputElement} id={errorId}>
            {error}
          </ErrorMessage>
        ) : null}
      </VerticalStack>

      <InputDropdown
        className={menuClassName}
        getItemProps={getItemProps as any}
        highlightedIndex={highlightedIndex}
        inputId={id}
        options={options}
        pagination={pagination}
        selectedItemValue={selectedItem?.value}
        {...(listboxProps as HTMLAttributes<HTMLDivElement>)}
      />
    </VerticalStack>
  );
}
